import axios from 'src/utils/httpClients/axios';
import { mapJsonArray } from 'src/utils/mapUtils';

import { AUTH0_API } from 'src/config';
import { Tenant } from 'src/@types/tenants/tenant';
import { PagingFilter } from 'src/@types/shared/pagingFilter';
import { PagedResponse } from 'src/@types/shared/pagedResponse';
import { TenantCreate, TenantUpdate, InviteAdminModel } from 'src/@types/tenants/types';

// ----------------------------------------------------------------------

const baseUrl = `${AUTH0_API.domain}api/tenants`;
const apiUrl = `${AUTH0_API.domain}api/key`;

async function getPagedTenants(filter: PagingFilter) {
  const response = await axios.get<PagedResponse<Tenant>>(baseUrl, { params: { ...filter } });

  return {
    total: response.data.total,
    items: mapJsonArray(response.data.items, Tenant),
  };
}

async function getTenant(id: number) {
  const response = await axios.get<PagedResponse<Tenant>>(`${baseUrl}/${id}`);
  return new Tenant().parseJson(response.data);
}

async function createTenant(tenant: TenantCreate) {
  await axios.post(baseUrl, { ...tenant });
}

async function editTenant(tenant: TenantUpdate) {
  await axios.put(`${baseUrl}/${tenant.id}`, { ...tenant });
}

async function inviteAdmin(model: InviteAdminModel) {
  await axios.post(`${baseUrl}/${model.tenantId}/invite-admin`, { ...model });
}

async function setActivation(id: number, isDeactivated: boolean) {
  await axios.put(`${baseUrl}/${id}/set-activation`, { isDeactivated });
}

async function exportCsv() {
  const response = await axios.post(`${baseUrl}/export-csv`, {
    offset: 0,
    limit: 99999,
    timezoneOffset: new Date().getTimezoneOffset(),
  });
  return new Blob([response.data]);
}

const generateTenantApiKey = async (tenantId: number) => {
  const response = await axios.post(`${apiUrl}/${tenantId}/create`, { tenantId });

  return response.data;
};

const getKey = async (tenantId: number) => {
  const response = await axios.get(`${apiUrl}/${tenantId}`);

  return response.data;
};

const setKey = async (tenantId: number, revoked: boolean) => {
  await axios.put(`${apiUrl}`, { tenantId, revoked });
};

const setApiLimitPrice = async (
  tenantId: number,
  apiActionLimit: number,
  pricePerExceededApiAction: number
) => {
  await axios.post(`${apiUrl}/${tenantId}/business-properties/set`, {
    apiActionLimit,
    pricePerExceededApiAction,
  });
};

const getApiLimitPrice = async (tenantId: number) => {
  const response = await axios.get(`${apiUrl}/${tenantId}/business-properties`);

  return response.data;
};

const getEidEasySignatureMethods = async () => {
  const response = await axios.get(`${baseUrl}/signature-methods`);

  return response.data;
};

const aspTenantService = {
  getPagedTenants,
  getTenant,
  createTenant,
  editTenant,
  inviteAdmin,
  setActivation,
  exportCsv,
  generateTenantApiKey,
  getKey,
  setKey,
  setApiLimitPrice,
  getApiLimitPrice,
  getEidEasySignatureMethods,
};

export default aspTenantService;
